import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getParentDetails } from '../../../redux/parentRelated/parentHandle';
import {
    Paper, Box, Typography, Avatar, Grid, Chip, Divider, IconButton, Container, CircularProgress, Card, CardContent
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupIcon from '@mui/icons-material/Group';
import WorkIcon from '@mui/icons-material/Work';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import styled, { keyframes } from 'styled-components';
import { GreenButton } from '../../../components/buttonStyles';

const ParentDetails = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();

    const { parent, loading, error } = useSelector((state) => state.parent);

    useEffect(() => {
        dispatch(getParentDetails(id));
    }, [id, dispatch]);

    if (loading) {
        return (
            <LoadingWrapper>
                <CircularProgress size={60} />
                <Typography sx={{ mt: 2, color: '#667eea', fontWeight: 600 }}>Loading Parent Profile...</Typography>
            </LoadingWrapper>
        );
    }

    if (error || !parent) {
        return (
            <LoadingWrapper>
                <Typography color="error">{error ? "Error loading parent details" : "Parent not found"}</Typography>
                <GreenButton onClick={() => navigate("/Admin/parents")} sx={{ mt: 2 }}>Back to List</GreenButton>
            </LoadingWrapper>
        );
    }

    const InfoItem = ({ icon, label, value }) => (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {icon} {label}
            </Typography>
            <Typography variant="body1" sx={{ color: '#1a1a2e', fontWeight: 500 }}>
                {value || 'N/A'}
            </Typography>
        </Box>
    );

    return (
        <PageContainer>
            <HeroSection elevation={3}>
                <Container maxWidth="xl">
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <IconButton
                            onClick={() => navigate("/Admin/parents")}
                            sx={{ color: 'white', mr: 2, bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)' }}>Parent Profile</Typography>
                    </Box>

                    <Grid container spacing={3} alignItems="center">
                        <Grid item>
                            <StyledAvatar
                                src={parent.photo ? `${process.env.REACT_APP_BASE_URL}/${parent.photo}` : ''}
                            >
                                <FamilyRestroomIcon sx={{ fontSize: 60 }} />
                            </StyledAvatar>
                        </Grid>
                        <Grid item xs>
                            <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 1 }}>
                                {parent.fatherName}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                    label="Parent Account"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                />
                                <Chip
                                    icon={<EmailIcon sx={{ fontSize: '1rem !important', color: 'white !important' }} />}
                                    label={parent.email || 'No Email'}
                                    sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </HeroSection>

            <MainContent maxWidth="xl">
                <Grid container spacing={4} sx={{ mt: 5 }}>
                    {/* Left Column: Family Info */}
                    <Grid item xs={12} lg={8}>
                        <SectionTitle variant="h5">Family Information</SectionTitle>

                        <Grid container spacing={3}>
                            {/* Father's Card */}
                            <Grid item xs={12} md={6}>
                                <InfoCard elevation={2}>
                                    <CardHeader color="#667eea">
                                        <Typography variant="h6">Father's Details</Typography>
                                        <PersonIcon />
                                    </CardHeader>
                                    <CardContent>
                                        <InfoItem icon={<PersonIcon size="small" />} label="Father Name" value={parent.fatherName} />
                                        <InfoItem icon={<WorkIcon size="small" />} label="Occupation" value={parent.fatherOccupation} />
                                        <InfoItem icon={<PhoneIcon size="small" />} label="Phone" value={parent.fatherPhone} />
                                        <InfoItem icon={<EmailIcon size="small" />} label="Email" value={parent.fatherEmail} />
                                    </CardContent>
                                </InfoCard>
                            </Grid>

                            {/* Mother's Card */}
                            <Grid item xs={12} md={6}>
                                <InfoCard elevation={2}>
                                    <CardHeader color="#f093fb">
                                        <Typography variant="h6">Mother's Details</Typography>
                                        <PersonIcon />
                                    </CardHeader>
                                    <CardContent>
                                        <InfoItem icon={<PersonIcon size="small" />} label="Mother Name" value={parent.motherName} />
                                        <InfoItem icon={<WorkIcon size="small" />} label="Occupation" value={parent.motherOccupation} />
                                        <InfoItem icon={<PhoneIcon size="small" />} label="Phone" value={parent.motherPhone} />
                                        <InfoItem icon={<EmailIcon size="small" />} label="Email" value={parent.motherEmail} />
                                    </CardContent>
                                </InfoCard>
                            </Grid>

                            {/* Guardian Card (if exists) */}
                            {parent.guardianName && (
                                <Grid item xs={12}>
                                    <InfoCard elevation={2}>
                                        <CardHeader color="#11998e">
                                            <Typography variant="h6">Guardian Details</Typography>
                                            <GroupIcon />
                                        </CardHeader>
                                        <CardContent>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <InfoItem label="Guardian Name" value={parent.guardianName} />
                                                    <InfoItem label="Relation" value={parent.guardianRelation} />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <InfoItem label="Phone" value={parent.guardianPhone} />
                                                    <InfoItem label="Email" value={parent.guardianEmail} />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <InfoItem label="Address" value={parent.guardianAddress} />
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </InfoCard>
                                </Grid>
                            )}

                            {/* Contact Card */}
                            <Grid item xs={12}>
                                <InfoCard elevation={2}>
                                    <CardHeader color="#f5576c">
                                        <Typography variant="h6">Primary Contact & Address</Typography>
                                        <LocationOnIcon />
                                    </CardHeader>
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <InfoItem label="Primary Phone" value={parent.phone} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <InfoItem label="Primary Email" value={parent.email} />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <InfoItem label="Residential Address" value={parent.address} />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </InfoCard>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Right Column: Linked Students */}
                    <Grid item xs={12} lg={4}>
                        <SectionTitle variant="h5">Linked Students</SectionTitle>

                        {parent.students && parent.students.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {parent.students.map((student) => (
                                    <StudentCard key={student._id} elevation={2}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar
                                                src={student.photo ? `${process.env.REACT_APP_API_URL}/${student.photo}` : ''}
                                                sx={{ width: 50, height: 50, border: '2px solid #667eea' }}
                                            />
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                                                    {student.name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Roll: {student.rollNum} • Class: {student.sclassName?.sclassName || 'N/A'}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                onClick={() => navigate(`/Admin/students/student/${student._id}`)}
                                                sx={{ bgcolor: '#f0f4ff', color: '#667eea', '&:hover': { bgcolor: '#e0e7ff' } }}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </StudentCard>
                                ))}
                            </Box>
                        ) : (
                            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '20px', bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
                                <GroupIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                                <Typography color="textSecondary" gutterBottom>No students linked yet.</Typography>
                                <GreenButton
                                    size="small"
                                    onClick={() => navigate(`/Admin/parents/link/${id}`)}
                                    sx={{ mt: 1 }}
                                >
                                    Link Student
                                </GreenButton>
                            </Paper>
                        )}
                    </Grid>
                </Grid>
            </MainContent>
        </PageContainer>
    );
};

export default ParentDetails;

// Styled Components
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
    background-color: #f8fafc;
    min-height: 100vh;
`;

const HeroSection = styled(Paper)`
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%) !important;
    border-radius: 0 0 40px 40px !important;
    padding: 60px 0 !important;
    color: white !important;
`;

const StyledAvatar = styled(Avatar)`
    width: 140px !important;
    height: 140px !important;
    border: 6px solid rgba(255, 255, 255, 0.1);
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    box-shadow: 0 8px 25px rgba(0,0,0,0.2);
`;

const MainContent = styled(Container)`
    margin-top: -40px;
    padding-bottom: 60px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const SectionTitle = styled(Typography)`
    font-weight: 800 !important;
    color: #1a1a2e;
    margin-bottom: 24px !important;
    padding-left: 8px;
    border-left: 5px solid #667eea;
`;

const InfoCard = styled(Paper)`
    border-radius: 24px !important;
    overflow: hidden;
    height: 100%;
    border: 1px solid #edf2f7;
`;

const CardHeader = styled(Box)`
    padding: 16px 24px;
    background: ${props => props.color || '#667eea'};
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    svg { opacity: 0.8; }
`;

const StudentCard = styled(Paper)`
    padding: 16px;
    border-radius: 20px !important;
    border: 1px solid #edf2f7;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateX(5px);
        box-shadow: 0 4px 15px rgba(0,0,0,0.08) !important;
    }
`;

const LoadingWrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 80vh;
`;
